import { useState } from 'react';

import type { Cents } from '@/shared/format/money';

/** A selected row's identifier and amount in integer cents. */
export interface SelectedAmountRow {
  readonly id: string;
  readonly amount?: Cents | null;
}

/** Totals visited pages by scope; placeholder rows must not populate a new tenant's cache. */
export function useSelectedRowsTotal(
  rows: readonly SelectedAmountRow[],
  selectedIds: readonly string[],
  scope?: string,
  isPlaceholderData = false,
): Cents {
  const [cache, setCache] = useState<{
    scope: string | undefined;
    amounts: ReadonlyMap<string, Cents>;
  }>(() => ({
    scope,
    amounts: new Map(),
  }));
  let amounts = cache.scope === scope ? cache.amounts : new Map<string, Cents>();
  for (const row of isPlaceholderData ? [] : rows) {
    const amount = row.amount ?? 0;
    if (!amounts.has(row.id) || amounts.get(row.id) !== amount) {
      amounts = new Map(amounts).set(row.id, amount);
    }
  }
  if (amounts !== cache.amounts || scope !== cache.scope) setCache({ scope, amounts });
  return selectedIds.reduce((total, id) => total + (amounts.get(id) ?? 0), 0);
}
